/**
 * CorrectionForm — inline human-correction UI for a single cluster.
 *
 * Three actions:
 *   - Confirm  → PATCH { action: 'confirm' } (trust_level=human_confirmed,
 *                human_status=reviewed_ok)
 *   - Override → PATCH { action: 'override', override: {…changed fields…} }
 *                (writes human_override, trust_level=human_overridden,
 *                appends to unserved_church_corrections)
 *   - Reject   → PATCH { action: 'reject' } (trust_level=human_rejected,
 *                cluster hidden from view afterwards)
 *
 * The form prefills with the cluster's current effective values. On Override,
 * only fields that have changed from the initial prefill are sent in the
 * override body — minimizing what gets written and keeping the audit trail
 * clean.
 */

import { useMemo, useState } from 'react';
import { churchDemandAPI } from '../../../../services/api';

const OVERRIDE_FIELDS = [
  { key: 'canonical_name', label: 'Canonical name' },
  { key: 'denomination', label: 'Denomination' },
  { key: 'city', label: 'City' },
  { key: 'state', label: 'State' },
  { key: 'address', label: 'Address' },
  { key: 'website', label: 'Website' },
  { key: 'phone', label: 'Phone' },
];

// Build a stable initial-values object from the cluster prop so we can
// diff form values against it to determine what's changed.
function initialValuesFromCluster(cluster) {
  const out = {};
  for (const { key } of OVERRIDE_FIELDS) {
    out[key] = cluster?.[key] ?? '';
  }
  return out;
}

// Diff the form values against the initial prefill — returns an object
// containing only the fields whose values differ.
function changedFields(values, initial) {
  const out = {};
  for (const { key } of OVERRIDE_FIELDS) {
    const curr = (values[key] || '').trim();
    const init = (initial[key] || '').trim();
    if (curr !== init) {
      out[key] = curr;
    }
  }
  return out;
}

export default function CorrectionForm({ cluster, onSuccess, onCancel, prefillOverride }) {
  const initial = useMemo(() => initialValuesFromCluster(cluster), [cluster]);
  // `prefillOverride` lets callers (e.g. "Use this match" on the Places
  // candidate panel) seed non-empty fields on top of the cluster's current
  // values. The diff baseline `initial` stays the cluster snapshot, so only
  // the prefilled fields that actually differ from the cluster get sent.
  const [values, setValues] = useState(() => ({
    ...initial,
    ...(prefillOverride || {}),
  }));
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(null); // 'confirm' | 'override' | 'reject' | null
  const [error, setError] = useState(null);

  const handleChange = (key) => (e) => {
    setValues((prev) => ({ ...prev, [key]: e.target.value }));
  };

  async function submit(action) {
    if (!cluster?.id) {
      setError('Cannot submit: cluster id is missing.');
      return;
    }

    // Reject requires explicit confirmation — it's destructive (hides the cluster).
    if (action === 'reject') {
      const confirmed = window.confirm(
        'Reject this cluster? It will be hidden from the view and cannot be easily undone.'
      );
      if (!confirmed) return;
    }

    const body = { action };

    if (action === 'override') {
      const diff = changedFields(values, initial);
      if (Object.keys(diff).length === 0) {
        setError('No fields changed. Edit at least one value before clicking Override.');
        return;
      }
      body.override = diff;
    }

    if (note.trim()) {
      body.note = note.trim();
    }

    setSubmitting(action);
    setError(null);
    try {
      const res = await churchDemandAPI.correct(cluster.id, body);

      // When an override changes canonical_name/city/state/address, the
      // backend re-queries Google Places for fresh coords. If that lookup
      // failed (zero results, daily cap, network/API error), the metadata
      // correction still committed but the map pin will stay stale until
      // the admin overrides again with better values. Surface the failure
      // via window.alert so the admin knows to retry if they care.
      if (action === 'override') {
        const pr = res?.data?.places_refresh;
        if (pr && pr.status !== 'success' && pr.status !== 'not_needed') {
          if (pr.status === 'zero_results') {
            window.alert(
              "Correction saved, but Google Places couldn't find a matching " +
                'church at the new location. The map coordinates were not ' +
                'updated — you can override again with more specific values ' +
                'if needed.'
            );
          } else if (pr.status === 'skipped_cap') {
            window.alert(
              "Correction saved, but today's Google Places call budget is " +
                'exhausted. The map coordinates will update on tomorrow\'s ' +
                'nightly recluster, or you can override again after the ' +
                'budget resets.'
            );
          } else if (pr.status === 'failed') {
            window.alert(
              'Correction saved, but the Places lookup failed (' +
                (pr.reason || 'unknown error') +
                '). The map coordinates were not updated. You can try ' +
                'overriding again or trigger a full recluster.'
            );
          }
        }
      }

      if (onSuccess) onSuccess(res.data);
    } catch (err) {
      setError(
        err?.response?.data?.error ||
          err?.message ||
          `Failed to ${action} cluster`
      );
    } finally {
      setSubmitting(null);
    }
  }

  const busy = submitting !== null;

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-5 space-y-4">
      <div className="flex items-start justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Correct this cluster</h3>
          <p className="text-sm text-gray-500 mt-0.5">
            Override fields below, then choose an action. Only changed fields are sent.
          </p>
        </div>
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="text-sm text-gray-500 hover:text-gray-700 disabled:opacity-40"
          >
            Cancel
          </button>
        )}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 rounded-lg px-3 py-2 text-sm">
          <strong className="font-semibold">Error:</strong> {error}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {OVERRIDE_FIELDS.map(({ key, label }) => (
          <label key={key} className="flex flex-col gap-1">
            <span className="text-xs font-semibold text-gray-600 uppercase tracking-wider">
              {label}
            </span>
            <input
              type="text"
              value={values[key]}
              onChange={handleChange(key)}
              disabled={busy}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50"
            />
          </label>
        ))}
      </div>

      <label className="flex flex-col gap-1">
        <span className="text-xs font-semibold text-gray-600 uppercase tracking-wider">
          Note (optional)
        </span>
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          disabled={busy}
          rows={2}
          placeholder="Why are you correcting this? (recommended for Reject)"
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50 resize-none"
        />
      </label>

      <div className="flex flex-wrap gap-2 pt-2 border-t border-gray-100">
        <button
          type="button"
          onClick={() => submit('confirm')}
          disabled={busy}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed min-h-[44px]"
        >
          {submitting === 'confirm' ? 'Submitting…' : 'Confirm as-is'}
        </button>
        <button
          type="button"
          onClick={() => submit('override')}
          disabled={busy}
          className="px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700 disabled:opacity-40 disabled:cursor-not-allowed min-h-[44px]"
        >
          {submitting === 'override' ? 'Submitting…' : 'Override'}
        </button>
        <button
          type="button"
          onClick={() => submit('reject')}
          disabled={busy}
          className="ml-auto px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed min-h-[44px]"
        >
          {submitting === 'reject' ? 'Submitting…' : 'Reject'}
        </button>
      </div>
    </div>
  );
}
